import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import Modal from './modal';
import { listStoreUsers, updateStoreUserApprover } from '../utils/queries';

// Lets an admin flag which active store ADMINs are order approvers, without
// leaving Store Settings for Users & Permissions. Triggered from
// GeneralTab.js when "Require order approval" is turned on, or reopened any
// time via its "Manage Approvers" link.
export default function ApproverSelectionModal({ open, setOpen, storeId, onSaved }) {
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [users, setUsers] = useState([]);
	const [selected, setSelected] = useState(() => new Set());

	useEffect(() => {
		if (!open) return;
		let cancelled = false;
		setLoading(true);
		listStoreUsers({ storeId, limit: 200 }).then((result) => {
			if (cancelled) return;
			setLoading(false);
			if (!result?.success) {
				toast.error(result?.error?.message || 'Failed to load store users.');
				return;
			}
			const admins = (result.data.users || []).filter((u) => u.role === 'ADMIN' && u.activeFlag);
			setUsers(admins);
			setSelected(new Set(admins.filter((u) => u.isOrderApprover).map((u) => u.userId)));
		});
		return () => {
			cancelled = true;
		};
	}, [open, storeId]);

	const toggleUser = (userId) => {
		setSelected((current) => {
			const next = new Set(current);
			if (next.has(userId)) next.delete(userId);
			else next.add(userId);
			return next;
		});
	};

	const handleSave = async () => {
		setSaving(true);
		const changed = users.filter((u) => !!u.isOrderApprover !== selected.has(u.userId));
		const results = await Promise.all(
			changed.map((u) => updateStoreUserApprover(u.userId, storeId, selected.has(u.userId), u.role)),
		);
		setSaving(false);
		const failed = results.some((r) => !r?.success);
		if (failed) {
			toast.error('Some approver changes failed to save. Please try again.');
			return;
		}
		toast.success('Approvers updated.');
		setOpen(false);
		onSaved?.();
	};

	return (
		<Modal open={open} setOpen={setOpen} title='Manage Order Approvers' size='md'>
			<p className='text-sm text-gray-500'>
				Choose which store admins can approve or decline orders. Only active users with the ADMIN role are
				eligible.
			</p>

			{loading ? (
				<p className='text-sm text-gray-400 py-6'>Loading store users…</p>
			) : users.length === 0 ? (
				<p className='text-sm text-gray-400 py-6'>
					No eligible users found. Invite or promote a user in Users &amp;
					Permissions first.
				</p>
			) : (
				<div className='mt-4 border border-gray-200 rounded-lg overflow-hidden max-h-80 overflow-y-auto'>
					<table className='min-w-full divide-y divide-gray-200'>
						<thead className='bg-gray-50'>
							<tr>
								<th className='w-10 px-3 py-2'></th>
								<th className='text-left text-xs font-medium text-gray-500 uppercase px-3 py-2'>Name</th>
								<th className='text-left text-xs font-medium text-gray-500 uppercase px-3 py-2'>Email</th>
							</tr>
						</thead>
						<tbody className='divide-y divide-gray-100'>
							{users.map((u) => (
								<tr key={u.userId} className='hover:bg-gray-50 cursor-pointer' onClick={() => toggleUser(u.userId)}>
									<td className='px-3 py-2'>
										<input
											type='checkbox'
											checked={selected.has(u.userId)}
											onChange={() => toggleUser(u.userId)}
											onClick={(e) => e.stopPropagation()}
										/>
									</td>
									<td className='px-3 py-2 text-sm text-gray-900'>
										{[u.firstname, u.lastname].filter(Boolean).join(' ') || '—'}
									</td>
									<td className='px-3 py-2 text-sm text-gray-500'>{u.email}</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}

			<div className='flex justify-start gap-2 pt-6'>
				<button type='button' className='btn-secondary' onClick={() => setOpen(false)} disabled={saving}>
					Cancel
				</button>
				<button type='button' className='btn-primary' onClick={handleSave} disabled={saving || loading}>
					{saving ? 'Saving...' : 'Save Approvers'}
				</button>
			</div>
		</Modal>
	);
}
